import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Revision } from "@/pages/deals/types";
import IconContainer from "@/components/ui/icon-container";
import { Change } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { simpleDate } from "@/utils/datetime";
import {
  useRestoreDealQuestionnaireMutation,
  useDealsQuestionnairesResponses,
  useDealQuestionnaireQuestionOptions
} from "@/pages/deals/hooks";
import { useUsers } from "@/entities/users/hooks";
import { ReservedUserIds } from "@/entities/users/types";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { getFullName } from "@/entities/people/helpers";
import { PersonData } from "@/entities/people/types";

const TRANSLATION_KEY = "deals.dealView.filledByDreamer.previousVersion";

interface Props {
  dealId: string;
  questionnaireId: string;
  questionId: string;
  revision: Revision;
  setIsPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showRestoreButton?: boolean;
  showSources?: boolean;
}

const PreviousVersion: React.FC<Props> = ({
  dealId,
  questionnaireId,
  questionId,
  revision,
  setIsPopoverOpen,
  showRestoreButton = true,
  showSources = true
}) => {
  const { t } = useTranslation();

  const userIds = revision?.lastEditedBy ? [revision?.lastEditedBy] : [];
  const { users } = useUsers(userIds) || { users: [] };
  const [lastEditedByData] = users;

  const restoreQuestionnaire = useRestoreDealQuestionnaireMutation();

  const { refetch: refetchResponses } = useDealsQuestionnairesResponses(
    dealId,
    [questionnaireId]
  );

  const handleRestoreVersion = () => {
    restoreQuestionnaire.mutate(
      {
        pathParams: { dealId, questionnaireId },
        data: {
          questionIds: [questionId]
        }
      },
      {
        onSuccess: async (data) => {
          setIsPopoverOpen(false);
          if (data.successfulIds?.length > 0) {
            await refetchResponses();
          }
        }
      }
    );
  };

  const getSourceCount = () => {
    if (!showSources) return 0;
    // We currently only support a single activity source
    return revision?.response?.activityId ? 1 : 0;
  };

  const sourceCount = getSourceCount();

  const rawValue = revision?.response?.value;

  const multiSelectIds = useMemo(() => {
    if (!Array.isArray(rawValue)) return [];
    const ids = rawValue.filter(
      (v): v is string => typeof v === "string" && v.startsWith("dqo-")
    );
    return ids.length > 0 ? ids : [];
  }, [rawValue]);

  const isMultiSelect = multiSelectIds.length > 0;

  const { data: optionsData } = useDealQuestionnaireQuestionOptions(
    questionId,
    isMultiSelect
  );

  const resolvedMultiSelectValue = useMemo(() => {
    if (!isMultiSelect || !optionsData) return null;
    return multiSelectIds
      .map((id) => optionsData.find((opt) => opt.id === id)?.option ?? id)
      .join(", ");
  }, [isMultiSelect, optionsData, multiSelectIds]);

  const valueId = !isMultiSelect ? rawValue?.toString() || "" : "";
  const usePeopleQuery = getEntityHook(EntityType.PERSON);
  const peopleQuery = usePeopleQuery
    ? usePeopleQuery(valueId ? [valueId] : [])
    : { entities: [], isSuccess: false };

  const { entities: [personData] = [], isSuccess: isPersonSuccess } =
    peopleQuery;

  const displayValue = useMemo(() => {
    if (resolvedMultiSelectValue) return resolvedMultiSelectValue;
    if (isPersonSuccess && personData)
      return getFullName(personData as PersonData);
    return rawValue?.toString() || "";
  }, [resolvedMultiSelectValue, isPersonSuccess, personData, rawValue]);

  return (
    <div className="flex flex-col p-2 gap-2 bg-surface-focus-selected">
      <Text variant="bodySecondary">
        {t(`${TRANSLATION_KEY}.userVersion`, {
          userName:
            revision?.lastEditedBy === ReservedUserIds.DREAMER_CONVERSATIONAL_ID
              ? t("dreamer")
              : lastEditedByData?.firstName
        })}
      </Text>
      {revision?.response?.value && (
        <Text className="max-h-[14rem] overflow-y-auto italic">
          {displayValue}
        </Text>
      )}
      {revision?.createdAt && (
        <Text variant="bodySecondary">
          {t(`${TRANSLATION_KEY}.generatedOnWithSources`, {
            date: simpleDate(new Date(revision?.createdAt)),
            count: sourceCount
          })}
        </Text>
      )}
      {showRestoreButton && (
        <Button
          variant="tertiary"
          className="gap-[6px]"
          onClick={handleRestoreVersion}
        >
          <IconContainer size="5" color="cta">
            <Change />
          </IconContainer>
          <span>{t(`${TRANSLATION_KEY}.restoreVersion`)}</span>
        </Button>
      )}
    </div>
  );
};

export default PreviousVersion;
